import { FacetItem, ProductsFilterItem } from '@api/catalog/types/products';

import { FilterValue } from '@components/Filters/ProductFilter/types';

import { BrandItem, BrandsResponse, FacetResponse, FacetValue } from './types';

const SERVICE_KEYS = new Set([
    'category_id',
    'virtual_category_id',
    'price_gte',
    'price_lte',
    'availability_status',
    'is_profitable',
    'is_hit',
]);

export function mergeFacetResponses(
    base: FacetResponse,
    current: FacetResponse,
    previous: FacetResponse,
    lastChangedBlockCode?: string
): FacetItem[] {
    const getProductsCount = (value: FacetValue): number => {
        if (value.products_count !== undefined) {
            return value.products_count;
        }

        if (value.offers_count !== undefined) {
            return value.offers_count;
        }

        return 0;
    };

    const mapFrom = (facetResponse: FacetResponse) => {
        const facetBlocksMap = new Map<string, Map<string | number, FacetValue>>();
        facetResponse.forEach(facetBlock => {
            const facetValuesMap = new Map<string | number, FacetValue>();

            if (facetBlock.values) {
                facetBlock.values.forEach(facetValue => {
                    const productsCount = getProductsCount(facetValue);
                    facetValuesMap.set(facetValue.code, { ...facetValue, products_count: productsCount });
                });
            }

            facetBlocksMap.set(String(facetBlock.code), facetValuesMap);
        });

        return facetBlocksMap;
    };

    const currentMap = mapFrom(current);
    const previousMap = mapFrom(previous);

    const result: FacetItem[] = [];
    const seenBlockCodes = new Set<string>();

    for (const facetBlock of base) {
        const blockCode = String(facetBlock.code);
        const sourceBlocksMap = blockCode === lastChangedBlockCode ? previousMap : currentMap;
        const sourceFacetValuesMap = sourceBlocksMap.get(blockCode);

        const nonZero: FacetValue[] = [];
        const zero: FacetValue[] = [];

        for (const facetValue of facetBlock.values || []) {
            const sourceFacetValue = sourceFacetValuesMap?.get(facetValue.code);
            const productsCount = sourceFacetValue ? getProductsCount(sourceFacetValue) : 0;

            const mergedValue: FacetValue = {
                ...facetValue,
                products_count: productsCount,
            };

            if (productsCount === 0) {
                zero.push(mergedValue);
            } else {
                nonZero.push(mergedValue);
            }
        }

        result.push({
            name: facetBlock.name || '',
            code: facetBlock.code,
            values: [...nonZero, ...zero].map(value => ({
                name: value.name || '',
                code: value.code,
                offers_count: value.offers_count ?? 0,
                products_count: value.products_count ?? 0,
                ...(value.value !== undefined && { value: value.value }),
            })),
        });
        seenBlockCodes.add(blockCode);
    }

    const addMissingFrom = (facetBlocksMap: Map<string, Map<string | number, FacetValue>>) => {
        for (const [blockCode, facetValuesMap] of facetBlocksMap) {
            if (seenBlockCodes.has(blockCode)) continue;

            const nonZero: FacetValue[] = [];
            const zero: FacetValue[] = [];

            for (const facetValue of facetValuesMap.values()) {
                const productsCount = getProductsCount(facetValue);

                const value: FacetValue = {
                    ...facetValue,
                    products_count: productsCount,
                };

                if (productsCount === 0) {
                    zero.push(value);
                } else {
                    nonZero.push(value);
                }
            }

            result.push({
                name: '',
                code: blockCode,
                values: [...nonZero, ...zero].map(value => ({
                    name: value.name || '',
                    code: value.code,
                    offers_count: value.offers_count ?? 0,
                    products_count: value.products_count ?? 0,
                    ...(value.value !== undefined && { value: value.value }),
                })),
            });
            seenBlockCodes.add(blockCode);
        }
    };

    addMissingFrom(currentMap);
    addMissingFrom(previousMap);

    return result;
}

export function splitLastFilter(filters: Record<string, FilterValue>): {
    withoutLast: Record<string, FilterValue>;
    lastKey?: string;
} {
    const filterKeys = Object.keys(filters).filter(key => !SERVICE_KEYS.has(key));

    if (filterKeys.length === 0) {
        return { withoutLast: filters, lastKey: undefined };
    }

    const lastKey = filterKeys[filterKeys.length - 1];
    const withoutLast = { ...filters };
    delete withoutLast[lastKey];

    return { withoutLast, lastKey };
}

export function mergeBrandsResponses(
    base: BrandsResponse,
    current: BrandsResponse,
    previous: BrandsResponse,
    lastChangedBlockCode?: string
): ProductsFilterItem['brands'] {
    const getProductsCount = (brand: BrandItem) => brand.products_count ?? brand.offers_count ?? 0;

    const sortBrands = (brands: ProductsFilterItem['brands']) => {
        const nonZero: ProductsFilterItem['brands'] = [];
        const zero: ProductsFilterItem['brands'] = [];

        for (const brand of brands) {
            const count = brand.products_count ?? brand.offers_count;
            if (count === 0) {
                zero.push(brand);
            } else {
                nonZero.push(brand);
            }
        }

        return [...nonZero, ...zero];
    };

    const mapFrom = (brandsResponse: BrandsResponse) => {
        const brandsMap = new Map<number, BrandItem>();
        for (const brand of brandsResponse) {
            const productsCount = getProductsCount(brand);
            brandsMap.set(brand.id, { ...brand, products_count: productsCount });
        }

        return brandsMap;
    };

    const currentMap = mapFrom(current);
    const previousMap = mapFrom(previous);

    const result: ProductsFilterItem['brands'] = [];
    const seenBrandIds = new Set<number>();

    const sourceBrandsMap = lastChangedBlockCode === 'brand_id' ? previousMap : currentMap;

    for (const brand of base) {
        const sourceBrand = sourceBrandsMap.get(brand.id);
        const productsCount = sourceBrand?.products_count ?? sourceBrand?.offers_count ?? 0;
        result.push({
            id: brand.id,
            code: brand.code,
            name: brand.name,
            offers_count: brand.offers_count ?? 0,
            products_count: productsCount,
        });
        seenBrandIds.add(brand.id);
    }

    const addMissingFrom = (brandsMap: Map<number, BrandItem>) => {
        for (const brand of brandsMap.values()) {
            if (seenBrandIds.has(brand.id)) continue;
            const productsCount = getProductsCount(brand);
            result.push({
                id: brand.id,
                code: brand.code,
                name: brand.name,
                offers_count: brand.offers_count ?? 0,
                products_count: productsCount,
            });
            seenBrandIds.add(brand.id);
        }
    };

    addMissingFrom(currentMap);
    addMissingFrom(previousMap);

    return sortBrands(result);
}

export function mergeProductsFilterResponses(
    base: ProductsFilterItem,
    current: ProductsFilterItem,
    previous: ProductsFilterItem,
    lastChangedBlockCode?: string
): ProductsFilterItem {
    const mergedBrands = mergeBrandsResponses(
        base.brands || [],
        current.brands || [],
        previous.brands || [],
        lastChangedBlockCode
    );

    const mergedFacets = mergeFacetResponses(
        base.facet_props || [],
        current.facet_props || [],
        previous.facet_props || [],
        lastChangedBlockCode
    );

    return {
        brands: mergedBrands,
        facet_props: mergedFacets,
        products_count: current.products_count ?? base.products_count ?? 0,
        min_price: current.min_price ?? base.min_price ?? 0,
        max_price: current.max_price ?? base.max_price ?? 0,
    };
}
